import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, Check, X } from "lucide-react";
import { API } from "../lib/api";
import { sortByName } from "../lib/sortByName";
import { SearchableSelect } from "../components/SearchableSelect";
import { useConfirm } from "../components/ConfirmDialog";

const Blacklist = () => {
  const [blacklists, setBlacklists] = useState([]);
  const [confirm, confirmDialog] = useConfirm();
  const [prestadores, setPrestadores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    prestador_id: '',
    clientes_ids: ['global'], // Array para múltiplos clientes
    motivo: ''
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [blacklistRes, prestadoresRes, clientesRes] = await Promise.all([
        axios.get(`${API}/blacklist`),
        axios.get(`${API}/prestadores`),
        axios.get(`${API}/clientes`)
      ]);
      
      setBlacklists(blacklistRes.data);
      // Ordenar prestadores ativos alfabeticamente por nome
      const prestadoresAtivos = prestadoresRes.data
        .filter(p => p.status === 'ativo')
        .sort(sortByName);
      setPrestadores(prestadoresAtivos);
      setClientes(clientesRes.data.filter(c => c.status === 'ativo'));
    } catch (error) {
      toast.error("Erro ao carregar dados do blacklist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const carregar = async () => { await fetchData(); };
    carregar();
  }, []);

  const resetForm = () => {
    setFormData({
      prestador_id: '',
      clientes_ids: ['global'],
      motivo: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.prestador_id || !formData.motivo.trim() || formData.clientes_ids.length === 0) {
      toast.error("Prestador, cliente(s) e motivo são obrigatórios");
      return;
    }

    try {
      let successCount = 0;
      let errors = [];

      // Se "global" está selecionado, criar apenas um blacklist global
      if (formData.clientes_ids.includes('global')) {
        const submitData = {
          prestador_id: formData.prestador_id,
          cliente_id: null, // null = blacklist global
          motivo: formData.motivo.trim()
        };

        await axios.post(`${API}/blacklist`, submitData);
        successCount = 1;
        toast.success("Prestador adicionado ao blacklist GLOBAL (todos os clientes)!");
      } else {
        // Criar blacklist para cada cliente selecionado
        for (const clienteId of formData.clientes_ids) {
          try {
            const submitData = {
              prestador_id: formData.prestador_id,
              cliente_id: clienteId,
              motivo: formData.motivo.trim()
            };

            await axios.post(`${API}/blacklist`, submitData);
            successCount++;
          } catch (error) {
            const clienteNome = clientes.find(c => c.id === clienteId)?.nome || clienteId;
            errors.push(`${clienteNome}: ${error.response?.data?.detail || 'Erro desconhecido'}`);
          }
        }

        if (successCount > 0) {
          toast.success(`Prestador adicionado ao blacklist para ${successCount} cliente(s)!`);
        }
        
        if (errors.length > 0) {
          toast.error(`Erros: ${errors.join('; ')}`);
        }
      }
      
      if (successCount > 0) {
        setIsDialogOpen(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Erro ao adicionar ao blacklist");
      }
    }
  };

  const handleToggleStatus = async (blacklistId, currentStatus) => {
    const action = currentStatus ? "retirar do blacklist" : "reativar no blacklist";
    const ok = await confirm({
      title: currentStatus ? "Retirar do blacklist?" : "Reativar blacklist?",
      description: currentStatus
        ? "Tem certeza que deseja RETIRAR este prestador do blacklist? Ele poderá receber diárias novamente."
        : "Tem certeza que deseja REATIVAR este blacklist? O prestador será bloqueado novamente.",
      confirmText: currentStatus ? "Retirar" : "Reativar",
      destructive: currentStatus,
    });
    if (!ok) return;
    try {
      await axios.put(`${API}/blacklist/${blacklistId}`, {
        ativo: !currentStatus
      });

      const successMsg = currentStatus
        ? "Prestador retirado do blacklist com sucesso! Agora pode receber diárias."
        : "Blacklist reativado com sucesso! Prestador bloqueado novamente.";

      toast.success(successMsg);
      fetchData();
    } catch (error) {
      toast.error(`Erro ao ${action}`);
    }
  };

  const handleDelete = async (blacklistId) => {
    const ok = await confirm({
      title: "Remover permanentemente?",
      description: "Tem certeza que deseja remover este blacklist permanentemente? Esta ação não pode ser desfeita.",
      confirmText: "Remover",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/blacklist/${blacklistId}`);
      toast.success("Blacklist removido com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao remover blacklist");
    }
  };

  const getPrestadorNome = (prestadorId) => {
    const prestador = prestadores.find(p => p.id === prestadorId);
    return prestador ? prestador.nome : 'Prestador não encontrado';
  };

  const getClienteNome = (clienteId) => {
    if (!clienteId) return 'GLOBAL (Todos os clientes)';
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? cliente.nome : 'Cliente não encontrado';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Se for data com horário (ISO string)
    if (dateString.includes('T')) {
      return new Date(dateString).toLocaleString('pt-BR');
    }
    // Se for apenas data (YYYY-MM-DD)
    const [ano, mes, dia] = dateString.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      {confirmDialog}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Blacklist de Prestadores</h1>
          <p className="text-muted-foreground">Gerencie prestadores bloqueados por cliente ou globalmente</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Adicionar ao Blacklist
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            Lista de Blacklists
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Total: {blacklists.length} registros
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Prestador</TableHead>
                <TableHead>Escopo</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Data Criação</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {blacklists.map((blacklist) => (
                <TableRow key={blacklist.id}>
                  <TableCell className="font-medium">
                    {getPrestadorNome(blacklist.prestador_id)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={blacklist.cliente_id ? 'secondary' : 'destructive'}>
                      {getClienteNome(blacklist.cliente_id)}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="truncate" title={blacklist.motivo}>
                      {blacklist.motivo}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={blacklist.ativo ? 'destructive' : 'secondary'}>
                      {blacklist.ativo ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(blacklist.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      {blacklist.ativo ? (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-400" 
                          onClick={() => handleToggleStatus(blacklist.id, blacklist.ativo)}
                          title="Retirar do Blacklist"
                          data-testid={`retirar-blacklist-${blacklist.id}`}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Retirar
                        </Button>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-400" 
                          onClick={() => handleToggleStatus(blacklist.id, blacklist.ativo)}
                          title="Reativar Blacklist"
                          data-testid={`reativar-blacklist-${blacklist.id}`}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Reativar
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-300" 
                        onClick={() => handleDelete(blacklist.id)}
                        title="Remover Permanentemente"
                        data-testid={`remover-blacklist-${blacklist.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add to Blacklist Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Prestador ao Blacklist</DialogTitle>
            <DialogDescription>
              Bloqueie um prestador para um cliente específico ou para todos os clientes
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="prestador">Prestador *</Label>
              <SearchableSelect
                options={prestadores
                  .sort(sortByName)
                  .map(prestador => ({
                    value: prestador.id,
                    label: prestador.nome
                  }))
                }
                value={formData.prestador_id}
                onValueChange={(value) => setFormData({...formData, prestador_id: value})}
                placeholder="Selecione o prestador"
                searchPlaceholder="Buscar prestador..."
                emptyText="Nenhum prestador encontrado"
                className="w-full"
              />
            </div>

            <div>
              <Label>Clientes *</Label>
              <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="cliente-global"
                    checked={formData.clientes_ids.includes('global')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Se selecionar global, limpar todos os outros
                        setFormData({...formData, clientes_ids: ['global']});
                      } else {
                        // Se desmarcar global, remover da lista
                        setFormData({...formData, clientes_ids: formData.clientes_ids.filter(id => id !== 'global')});
                      }
                    }}
                    className="rounded"
                  />
                  <Label htmlFor="cliente-global" className="font-semibold text-red-600 dark:text-red-400">
                    GLOBAL - Todos os clientes
                  </Label>
                </div>
                
                {!formData.clientes_ids.includes('global') && (
                  <>
                    <hr className="my-2" />
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">Ou selecione clientes específicos:</p>
                    {clientes.map((cliente) => (
                      <div key={cliente.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`cliente-${cliente.id}`}
                          checked={formData.clientes_ids.includes(cliente.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, clientes_ids: [...formData.clientes_ids, cliente.id]});
                            } else {
                              setFormData({...formData, clientes_ids: formData.clientes_ids.filter(id => id !== cliente.id)});
                            }
                          }}
                          className="rounded"
                        />
                        <Label htmlFor={`cliente-${cliente.id}`}>
                          {cliente.nome}
                        </Label>
                      </div>
                    ))}
                  </>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Selecione &quot;GLOBAL&quot; para bloquear para todos os clientes, ou selecione clientes específicos
              </p>
            </div>

            <div>
              <Label htmlFor="motivo">Motivo *</Label>
              <Textarea
                id="motivo"
                value={formData.motivo}
                onChange={(e) => setFormData({...formData, motivo: e.target.value})}
                placeholder="Descreva o motivo do blacklist..."
                rows={3}
                required
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setIsDialogOpen(false);
                resetForm();
              }}>
                Cancelar
              </Button>
              <Button type="submit">
                Adicionar ao Blacklist
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Blacklist;
